import { useState } from "react";

import type { components } from "../api/schema";
import { queryKeys } from "../lib/queryKeys";
import { traceTreeToWaterfallSpans } from "../lib/traces";
import { useListQuery } from "../lib/useListQuery";
import {
  Button,
  EmptyState,
  InlineError,
  Select,
  Spinner,
  Stack,
  Text,
  TextInput,
  TraceWaterfall,
} from "../ui";

type TranscriptListItem = components["schemas"]["TranscriptListItem"];

export interface TraceNodeLocatorPickerProps {
  agentId?: string;
  disabled: boolean;
  nodeId: string;
  onNodeIdChange: (nodeId: string) => void;
}

// ASSUMED(trace_node selector rule from #2125: exact span_id or exact span name selects one trace node; answers carry the resolved span_id.)
export function TraceNodeLocatorPicker({
  agentId,
  disabled,
  nodeId,
  onNodeIdChange,
}: TraceNodeLocatorPickerProps) {
  const [transcriptId, setTranscriptId] = useState("");
  const transcripts = useListQuery("/v1/transcripts", {
    enabled: agentId != null,
    queryKey: queryKeys.transcripts.list({ agent_id: agentId }),
    params: { agent_id: agentId },
  });
  const traces = useListQuery("/v1/traces", {
    enabled: transcriptId.length > 0,
    queryKey: queryKeys.traces.list({ transcript_id: transcriptId }),
    params: { transcript_id: transcriptId },
  });
  const sampleTranscripts = transcripts.items;
  const loadedTraces = traces.items;

  return (
    <Stack gap={3}>
      <Select
        description="Choose a sample transcript to browse its linked trace."
        disabled={disabled || agentId == null}
        label="Sample transcript"
        value={transcriptId}
        onChange={(event) => {
          setTranscriptId(event.currentTarget.value);
        }}
      >
        <option value="">Select a transcript</option>
        {sampleTranscripts.map((transcript: TranscriptListItem) => (
          <option key={transcript.id} value={transcript.id}>
            {transcript.id}
          </option>
        ))}
      </Select>
      {transcripts.isPending ? <Spinner label="Loading transcripts" /> : null}
      {transcripts.errorMessage ? (
        <InlineError>{transcripts.errorMessage}</InlineError>
      ) : null}
      {transcripts.hasNextPage ? (
        <Button
          disabled={transcripts.isFetchingNextPage}
          type="button"
          onClick={transcripts.fetchNextPage}
        >
          {transcripts.isFetchingNextPage
            ? "Loading..."
            : "Load more transcripts"}
        </Button>
      ) : null}
      {agentId == null ? (
        <Text variant="muted">Select an agent to browse sample traces.</Text>
      ) : null}
      {transcriptId.length > 0 && traces.isFetching ? (
        <Spinner label="Loading trace" />
      ) : null}
      {traces.errorMessage ? (
        <InlineError>{traces.errorMessage}</InlineError>
      ) : null}
      {loadedTraces.map((trace, index) => (
        <TraceWaterfall
          ariaLabel={`Trace node picker ${index.toString()}`}
          key={index}
          selectedSpanId={nodeId}
          spans={traceTreeToWaterfallSpans(trace.tree)}
          onSpanSelect={(span) => {
            onNodeIdChange(span.id);
          }}
        />
      ))}
      {traces.hasNextPage ? (
        <Button
          disabled={traces.isFetchingNextPage}
          type="button"
          onClick={traces.fetchNextPage}
        >
          {traces.isFetchingNextPage ? "Loading..." : "Load more traces"}
        </Button>
      ) : null}
      {transcriptId.length > 0 &&
      !traces.isPending &&
      traces.errorMessage === undefined &&
      loadedTraces.length === 0 ? (
        <EmptyState title="No linked trace spans" />
      ) : null}
      <TextInput
        description="Use a span ID, or an exact span name when the sample trace is unavailable."
        disabled={disabled}
        label="Trace node"
        required
        value={nodeId}
        onChange={(event) => {
          onNodeIdChange(event.currentTarget.value);
        }}
      />
    </Stack>
  );
}
